import {
  Accordion as ChakraAccordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { ButtonIcon, ButtonText } from '@morressier/alembic';
import React, { useEffect, useState } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { SubmissionDetailTab, SUBMISSION_DETAIL_TABS } from '.';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { useSubmissionDetails } from './SubmissionDetailsContext';
import { REVIEWS_TABS } from 'src/components/Submission/BoardMemberReviewsTab';
import {
  BACKOFFICE_TABS,
  IBackofficeTab
} from 'src/components/Submission/BackofficeTab/BackofficeTab';
import {
  INTEGRITY_TABS,
  IIntegrityTab
} from 'src/components/Submission/IntegrityTab';

interface Task {
  title: string;
  helpText: string;
  action?:
    | { type: 'display_text' }
    | { type: 'open_url'; url: string }
    | { type: 'tab_switch'; tabName: string; subTabName?: string };
}

interface PhaseChecklist {
  tasks?: Task[];
}

interface ChecklistData {
  articles?: Record<string, { phases?: Record<string, PhaseChecklist> }>;
}

interface Props {
  setActiveTab?: (tab: SubmissionDetailTab) => void;
}

export const SubmissionTasks: React.FC<Props> = ({ setActiveTab }) => {
  const { workflowResponse, fieldsResponse } = useSubmissionData();
  const { submissionContext } = useSubmissionContextData();
  const { setReviewsSubTab, setBackOfficeSubTab, setBackIntegritySubTab } =
    useSubmissionDetails();
  const [sectionToJumpTo, setSectionToJumpTo] = useState<string>();
  const checklistData = (
    submissionContext as typeof submissionContext & {
      articleTaskChecklistsData?: ChecklistData;
    }
  )?.articleTaskChecklistsData;

  const articleType = submissionContext?.articleTypes?.find(
    item => item.name === fieldsResponse.articleTypeName
  );
  const tasks =
    checklistData?.articles?.[articleType?.articleTypeCategoryId ?? '']
      ?.phases?.[workflowResponse.workflowData.currentState]?.tasks ?? [];

  useEffect(() => {
    if (!sectionToJumpTo) return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(sectionToJumpTo)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setSectionToJumpTo(undefined);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [sectionToJumpTo]);

  if (!tasks.length) return null;

  const handleTaskAction = (task: Task) => {
    if (task.action?.type === 'tab_switch') {
      const normalizedTabName = task.action.tabName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      const tab = (
        Object.values(SUBMISSION_DETAIL_TABS) as SubmissionDetailTab[]
      ).find(
        candidate =>
          candidate.toLowerCase().replace(/[^a-z0-9]/g, '') ===
          normalizedTabName
      );
      if (!tab) return;

      setActiveTab?.(tab);
      const subTabName = task.action.subTabName;
      if (!subTabName) return;

      const normalizedName = subTabName.toLowerCase().replace(/[^a-z0-9]/g, '');
      const matchesName = (key: string, label?: string) =>
        [key, label]
          .filter(Boolean)
          .some(
            value =>
              value!.toLowerCase().replace(/[^a-z0-9]/g, '') === normalizedName
          );

      if (tab === SUBMISSION_DETAIL_TABS.REVIEWS) {
        const subTab = Object.entries(REVIEWS_TABS).find(([key, value]) =>
          matchesName(key, value)
        )?.[1];
        if (subTab) setReviewsSubTab(subTab);
      } else if (tab === SUBMISSION_DETAIL_TABS.BACKOFFICE) {
        const subTab = Object.entries(BACKOFFICE_TABS).find(([key, value]) =>
          matchesName(key, value.label)
        )?.[1];
        if (subTab) setBackOfficeSubTab(subTab as IBackofficeTab);
      } else if (tab === SUBMISSION_DETAIL_TABS.INTEGRITY) {
        const subTab = Object.entries(INTEGRITY_TABS).find(([key, value]) =>
          matchesName(key, value.label)
        )?.[1];
        if (subTab) setBackIntegritySubTab(subTab as IIntegrityTab);
      }
      return;
    }

    if (task.action?.type !== 'open_url' || !task.action.url) return;

    let destination: URL;
    try {
      destination = new URL(task.action.url, window.location.origin);
    } catch {
      return;
    }

    const validTabs = Object.values(
      SUBMISSION_DETAIL_TABS
    ) as SubmissionDetailTab[];
    const pathSegments = destination.pathname.split('/').filter(Boolean);
    const requestedTab = destination.searchParams.get('activeTab');
    const tab = requestedTab
      ? validTabs.find(
          candidate => candidate.toLowerCase() === requestedTab.toLowerCase()
        )
      : pathSegments
          .map(segment =>
            validTabs.find(
              candidate => candidate.toLowerCase() === segment.toLowerCase()
            )
          )
          .find(Boolean);

    if (tab) setActiveTab?.(tab);

    let section =
      destination.searchParams.get('section') ||
      destination.searchParams.get('jumpToSection');
    if (!section && destination.hash) {
      try {
        section = decodeURIComponent(destination.hash.slice(1));
      } catch {
        section = destination.hash.slice(1);
      }
    }
    if (section) setSectionToJumpTo(section);
  };

  return (
    <ChakraAccordion allowToggle defaultIndex={0} width="100%">
      <AccordionItem
        border="1px solid"
        borderColor="neutralPrimaryBorder"
        borderRadius="4px"
        overflow="hidden"
      >
        <AccordionButton py={2} px={3} bgColor="surfaceSecondaryFill">
          <Flex
            width="100%"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text textStyle="headingLine">Tasks ({tasks.length})</Text>
            <AccordionIcon fontSize="sm" />
          </Flex>
        </AccordionButton>
        <AccordionPanel p={3} background="surfacePrimaryFill">
          <Flex direction="column" gap={1}>
            {tasks.map((task, index) => (
              <Flex
                key={`${task.title}-${index}`}
                justifyContent="space-between"
                gap={1}
                alignItems="center"
              >
                <Text textStyle="textContext">
                  {task.title}{' '}
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement="top"
                    label={task.helpText}
                    borderRadius={4}
                  >
                    <ButtonIcon
                      size="small"
                      ml={1}
                      ariaLabel="Help Icon"
                      icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    />
                  </Tooltip>
                </Text>
                {(task?.action?.type === 'open_url' ||
                  task?.action?.type === 'tab_switch') && (
                  <Flex justifyContent="flex-end">
                    <ButtonText onClick={() => handleTaskAction(task)} fontSize='sm'>
                      {task.title}
                    </ButtonText>
                  </Flex>
                )}
              </Flex>
            ))}
          </Flex>
        </AccordionPanel>
      </AccordionItem>
    </ChakraAccordion>
  );
};
